import { Money, formatDate, formatMoney } from '@betterment/core';
import { isToday } from 'date-fns';

import { useXYScales } from '../axis/useXYScales';
import { XAxis } from '../axis/XAxis';
import { YAxis } from '../axis/YAxis';
import { Grid } from '../Grid';
import { type BalanceChartData, BalanceChartDataSeries } from '../series/BalanceSeries';
import { XYChart, type XYChartProps } from '../XYChart';

type BalanceChartProps = Omit<XYChartProps, 'children' | 'width' | 'height'> & {
  children?: React.ReactNode;
  data: BalanceChartData;
  width: number;
  height: number;
};

const formatDateLabel = (value: Date) => {
  return isToday(value) ? 'Today' : formatDate(value, 'longOrdinalShortMonth');
};

const yTickFormat = (value: number) => formatMoney(Money(value), 'compact');

export const BalanceChart = ({
  children,
  margin = { right: 20, top: 20, left: 50, bottom: 50 },
  data,
  width,
  height,
  ...chartProps
}: BalanceChartProps) => {
  const allValues = data.flatMap(d => [d.balance.cents, d.invested.cents]);
  const dataMin = Math.min(...allValues);
  const dataMax = Math.max(...allValues);

  const { xScale, yScale } = useXYScales({
    yMin: dataMin,
    yMax: dataMax,
  });

  return (
    <XYChart width={width} height={height} {...chartProps} xScale={xScale} yScale={yScale} margin={margin}>
      <Grid />
      <XAxis tickFormat={formatDateLabel} />
      <YAxis tickFormat={yTickFormat} />
      <BalanceChartDataSeries data={data} />
      {children}
    </XYChart>
  );
};
